<template>
  <el-dialog :visible.sync="visible" width="1600px" :close-on-click-modal="false" :show-close="true" @closed="clearData"
    @opened="initMap" custom-class="charge-info-dialog">
    <div class="dialog-header">
      <el-button type="text" icon="el-icon-close" @click="closeDialog" class="close-btn"></el-button>
    </div>
    <div v-loading="loading" class="charge-info-container">
      <!-- 上方：地图、基本信息和充电记录布局 -->
      <el-row :gutter="20">
        <!-- 左侧：地图展示 -->
        <el-col :span="14">
          <el-card shadow="never" class="map-card">
            <div slot="header">
              <span v-if="realTimeState">位置信息 {{ realTimeState.driverNo }}</span>
            </div>
            <div id="chargeInfoMap" class="map-container"></div>
          </el-card>
        </el-col>

        <!-- 中间：基本信息 -->
        <el-col :span="5">
          <el-card shadow="never" class="info-card">
            <div slot="header">
              <span>基本信息</span>
            </div>
            <div class="info-item">
              <div class="label">充电状态：</div>
              <div class="value">
                <el-tag :type="chargeStatusType" size="medium">
                  {{ chargeStatusText }}
                  <span v-if="realTimeChargeInfo && realTimeChargeInfo.chargeStatus === 1">
                    - {{ realTimeChargeInfo.totalCur }} A
                  </span>
                </el-tag>

              </div>
            </div>
            <div class="info-item">
              <div class="label">最后订单时间：</div>
              <div class="value">
                <span v-if="lastOrderTime">
                  {{ lastOrderTime | dateFormat('yyyy-MM-dd hh:mm:ss') }}
                </span>
                <span v-else class="no-data">暂无数据</span>
              </div>
            </div>
            <div v-if="realTimeChargeInfo" class="info-item">
              <div class="label">剩余续航里程：</div>
              <div class="value" :class="leftRangeMileageClass">
                {{ realTimeChargeInfo.rangeMileage || 0 }} KM
                <span v-if="realTimeChargeInfo">
                  （电量：{{ realTimeChargeInfo.totalSoc || 0 }}%）
                </span>
              </div>
            </div>

            <!-- 行程续航预计信息 -->
            <template v-if="batterySummary && batterySummary.totalMileage > 0">
              <div class="info-divider"></div>
              <div class="info-item">
                <div class="label">行程总里程：</div>
                <div class="value">
                  {{ batterySummary.totalMileage || 0 }} KM
                </div>
              </div>
              <div v-if="batterySummary.expresswayMileage !== undefined && batterySummary.expresswayMileage !== null"
                class="info-item">
                <div class="label">高速里程：</div>
                <div class="value">
                  {{ batterySummary.expresswayMileage }} KM
                </div>
              </div>
              <div
                v-if="batterySummary.nonExpresswayMileage !== undefined && batterySummary.nonExpresswayMileage !== null"
                class="info-item">
                <div class="label">非高速里程：</div>
                <div class="value">
                  {{ batterySummary.nonExpresswayMileage }} KM
                </div>
              </div>
              <div v-if="batterySummary.needRangeMileage !== undefined && batterySummary.needRangeMileage !== null"
                class="info-item">
                <div class="label">转换里程：</div>
                <div class="value">
                  {{ batterySummary.needRangeMileage }} KM
                </div>
              </div>

            </template>
          </el-card>
        </el-col>

        <!-- 右侧：充电记录 -->
        <el-col :span="4">
          <el-card shadow="never" class="info-card">
            <div slot="header">
              <span>充电记录（最近1天）</span>
            </div>
            <div v-if="chargingRecordList && chargingRecordList.length > 0" class="charging-record-list">
              <ChargingRecordItem v-for="(record, index) in chargingRecordList" :key="record.id || index"
                :record="record" @click="handleRecordClick" />
            </div>
            <div v-else class="no-data-text">暂无充电记录</div>
          </el-card>
        </el-col>
      </el-row>

      <!-- 下方：电量状态图表 -->
      <div class="chart-container">
        <ChargeChart :chargeInfos="chargeInfos" :showCard="false" />
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { getChargeInfoDetail } from "@/api";
import ChargeChart from "@/components/ChargeChart";
import ChargingRecordItem from "./ChargingRecordItem";
import { DateUtil } from "@/util";

const carPng = require("@/assets/image/car_blue.png");
const carRedPng = require("@/assets/image/car_red.png");

export default {
  name: "DriverChargeInfoModal",
  components: {
    ChargeChart,
    ChargingRecordItem
  },
  data() {
    return {
      visible: false,
      loading: false,
      driverId: null,
      chargeInfos: [],
      realTimeChargeInfo: null,
      realTimeState: null,
      location: null,
      batterySummary: null,
      chargingRecordList: [],
      map: null,
      markers: [],
      recordMarker: null
    };
  },
  computed: {
    currentLocation() {
      // 直接使用接口返回的 location 字段
      return this.location;
    },
    chargeStatusText() {
      if (!this.realTimeChargeInfo) {
        return "未知";
      }
      const status = this.realTimeChargeInfo.chargeStatus;
      if (status === 1) {
        return "停车充电";
      } else if (status === 3) {
        return "未充电";
      }
      return "未知";
    },
    chargeStatusType() {
      if (!this.realTimeChargeInfo) {
        return "info";
      }
      const status = this.realTimeChargeInfo.chargeStatus;
      if (status === 1) {
        return "success";
      } else if (status === 3) {
        return "warning";
      }
      return "info";
    },
    lastOrderPosition() {
      if (this.realTimeState && this.realTimeState.latestRouteFinishPosition) {
        return this.realTimeState.latestRouteFinishPosition;
      }
      return null;
    },
    lastOrderTime() {
      if (this.realTimeState && this.realTimeState.latestOrderFinishTime) {
        return this.realTimeState.latestOrderFinishTime;
      }
      return null;
    },
    leftRangeMileageClass() {
      if (!this.batterySummary ||
        this.batterySummary.leftRangeMileage === undefined ||
        this.batterySummary.leftRangeMileage === null ||
        this.batterySummary.needRangeMileage === undefined ||
        this.batterySummary.needRangeMileage === null) {
        return '';
      }
      const diff = this.batterySummary.needRangeMileage - this.batterySummary.leftRangeMileage;
      if (diff > 50) {
        return 'range-warning-red';
      } else if (diff > 0) {
        return 'range-warning-yellow';
      }
      return '';
    }
  },
  methods: {
    show(driverId) {
      this.driverId = driverId;
      this.visible = true;
      // 如果地图已存在，先初始化
      if (this.map) {
        this.initMap();
      }
      this.loadData();
    },
    async loadData() {
      if (!this.driverId) {
        return;
      }
      this.loading = true;
      try {
        const res = await getChargeInfoDetail(this.driverId);
        const { chargeInfos = [], realTimeChargeInfo, realTimeState, location, batterySummary, chargingRecordList = [] } = res.data || {};
        this.chargeInfos = chargeInfos;
        this.realTimeChargeInfo = realTimeChargeInfo;
        this.realTimeState = realTimeState;
        this.location = location;
        this.batterySummary = batterySummary;
        this.chargingRecordList = chargingRecordList;

        // 更新地图标记
        this.$nextTick(() => {
          this.updateMapMarkers();
        });
      } catch (error) {
        console.error("加载充电信息失败:", error);
      } finally {
        this.loading = false;
      }
    },
    initMap() {
      // 确保地图容器存在
      if (!document.getElementById("chargeInfoMap")) {
        return;
      }
      if (this.map) {
        this.map.clearMap();
        this.markers = [];
      } else {
        this.map = new AMap.Map("chargeInfoMap", {
          resizeEnable: true,
          dragEnable: true,
          zoom: 13
        });
      }
      // 如果数据已加载，立即更新标记
      if (this.location || this.lastOrderPosition) {
        this.$nextTick(() => {
          this.updateMapMarkers();
        });
      }
    },
    updateMapMarkers() {
      if (!this.map) {
        return;
      }
      // 清除现有标记
      this.map.clearMap();
      this.markers = [];

      const positions = [];

      // 添加当前位置标记
      if (this.currentLocation && this.currentLocation.lat && this.currentLocation.lng) {
        const { lat, lng, gpsTime = "", vendor } = this.currentLocation || {};

        const time = gpsTime.substr(5);
        const locationSource = vendor === "jimi" ? "GPS" : "手机";
        const position = [parseFloat(lng), parseFloat(lat)];
        const driverNo = this.realTimeState.driverNo;
        positions.push(position);
        const currentMarker = new AMap.Marker({
          position,
          draggable: true,
          raiseOnDrag: true,
          anchor: "bottom-center",
          label: {
            direction: "top",
            content:
              "<div class='info'>" + `${driverNo} | ${time} | ${locationSource}` + "</div>"
          },
          icon: new AMap.Icon({
            size: new AMap.Size(40, 40),
            image: carPng,
            imageSize: new AMap.Size(40, 40)
          })
        });
        this.map.add(currentMarker);
        this.markers.push(currentMarker);
      }

      // 添加最后订单位置标记
      if (this.lastOrderPosition && this.lastOrderPosition.lat && this.lastOrderPosition.lng) {
        const position = [parseFloat(this.lastOrderPosition.lng), parseFloat(this.lastOrderPosition.lat)];
        positions.push(position);
        const orderTime = this.lastOrderTime ? DateUtil.dateStrFormat(this.lastOrderTime, "MM-dd hh:mm") : "";

        const iconImg = `/yxmgt/static/image/point/grey_st_1.png`;

        const lastOrderMarker = new AMap.Marker({
          position,
          draggable: true,
          raiseOnDrag: true,
          label: {
            direction: "top",
            content: `<div class='info'>最后订单位置${orderTime ? "<br/>" + orderTime : ""}</div>`
          },
          icon: new AMap.Icon({
            image: iconImg,
            imageSize: new AMap.Size(70, 56)
          }),
          anchor: "bottom-center",
        });
        this.map.add(lastOrderMarker);
        this.markers.push(lastOrderMarker);
      }

      // 添加最后充电位置标记
      if (this.realTimeChargeInfo && this.realTimeChargeInfo.chargePosition &&
        this.realTimeChargeInfo.chargePosition.lat && this.realTimeChargeInfo.chargePosition.lng) {
        const position = [
          parseFloat(this.realTimeChargeInfo.chargePosition.lng),
          parseFloat(this.realTimeChargeInfo.chargePosition.lat)
        ];
        positions.push(position);
        const chargeTime = this.realTimeChargeInfo.latestChargeTime
          ? DateUtil.dateStrFormat(this.realTimeChargeInfo.latestChargeTime, "MM-dd hh:mm")
          : "";
        const chargeSoc = this.realTimeChargeInfo.latestChargeSoc !== null
          ? this.realTimeChargeInfo.latestChargeSoc + "%"
          : "";
        const chargeLabel = `最后充电位置${chargeTime ? "<br/>" + chargeTime : ""}${chargeSoc ? "<br/>电量: " + chargeSoc : ""}`;

        const lastChargeMarker = new AMap.Marker({
          position,
          draggable: true,
          raiseOnDrag: true,
          anchor: "bottom-center",
          label: {
            direction: "top",
            content: `<div class='info'>${chargeLabel}</div>`
          },
          icon: new AMap.Icon({
            size: new AMap.Size(40, 40),
            image: carRedPng,
            imageSize: new AMap.Size(40, 40)
          })
        });
        this.map.add(lastChargeMarker);
        this.markers.push(lastChargeMarker);
      }

      // 清除之前点击记录产生的标记（updateMapMarkers 会 clearMap，此处仅重置引用）
      this.recordMarker = null;

      // 调整地图视野以包含所有标记
      if (positions.length > 0) {
        this.map.setFitView(this.markers);
      } else if (this.currentLocation && this.currentLocation.lat && this.currentLocation.lng) {
        // 如果只有当前位置，居中显示
        this.map.setCenter([parseFloat(this.currentLocation.lng), parseFloat(this.currentLocation.lat)]);
      }
    },
    handleRecordClick(record) {
      if (!this.map) return;
      // 有结束地址就用结束地址，没有就用开始地址
      const position = (record.endChargePosition && record.endChargePosition.lat && record.endChargePosition.lng)
        ? record.endChargePosition
        : (record.startChargePosition && record.startChargePosition.lat && record.startChargePosition.lng)
          ? record.startChargePosition
          : null;
      if (!position) {
        this.$message.info('该记录无地址信息');
        return;
      }
      const lng = parseFloat(position.lng);
      const lat = parseFloat(position.lat);
      const name = position.name || '充电位置';
      const timeStr = record.startTime
        ? (DateUtil.dateStrFormat(record.startTime, 'MM-dd hh:mm') + (record.endTime ? ' ~ ' + DateUtil.dateStrFormat(record.endTime, 'hh:mm') : ''))
        : '—';
      const capacityStr = (record.chargingCapacity !== undefined && record.chargingCapacity !== null)
        ? record.chargingCapacity + '%'
        : (record.startSoc != null && record.endSoc != null ? record.startSoc + '% ~ ' + record.endSoc + '%' : '—');

      if (this.recordMarker) {
        this.map.remove(this.recordMarker);
        this.recordMarker = null;
      }
      const marker = new AMap.Marker({
        position: [lng, lat],
        anchor: 'bottom-center',
        label: {
          direction: 'top',
          content: `<div class='info'>${name}<br/>充电时间：${timeStr}<br/>充电电量：${capacityStr}</div>`
        },
        icon: new AMap.Icon({
          size: new AMap.Size(40, 40),
          image: carRedPng,
          imageSize: new AMap.Size(40, 40)
        })
      });
      this.map.add(marker);
      this.recordMarker = marker;
      this.map.setCenter([lng, lat]);
      this.map.setZoom(16);
    },
    closeDialog() {
      this.visible = false;
    },

    clearData() {
      this.driverId = null;
      this.chargeInfos = [];
      this.realTimeChargeInfo = null;
      this.realTimeState = null;
      this.location = null;
      this.batterySummary = null;
      this.chargingRecordList = [];
      this.loading = false;
      this.recordMarker = null;
      if (this.map) {
        this.map.clearMap();
        this.markers = [];
      }
    }
  }
};
</script>

<style lang="less" scoped>
.charge-info-container {
  min-height: 400px;
}

.map-card,
.info-card {
  height: 100%;
}

.map-container {
  width: 100%;
  height: 400px;
}

.chart-container {
  padding: 10px 10px;
}

.info {
  font-size: 14px;
  font-weight: bold;
  color: #ff5722;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
}

.info-item {
  display: flex;
  margin-bottom: 12px;
  align-items: flex-start;

  .range-warning-red {
    color: #F56C6C !important;
    font-weight: bold;
  }

  .range-warning-yellow {
    color: #E6A23C !important;
    font-weight: bold;
  }

  .label {
    width: 100px;
    font-weight: bold;
    color: #606266;
    flex-shrink: 0;
    font-size: 14px;
  }

  .value {
    flex: 1;
    color: #303133;
    font-size: 14px;

    .soc-text {
      margin-left: 10px;
      color: #909399;
      font-size: 14px;
    }

    .no-data {
      color: #909399;
      font-style: italic;
    }


  }
}

.info-divider {
  height: 1px;
  background: #EBEEF5;
  margin: 15px 0;
}

.charging-record-list {
  max-height: 400px;
  overflow-y: auto;
}

.no-data-text {
  text-align: center;
  color: #909399;
  font-style: italic;
  padding: 20px 0;
  font-size: 14px;
}

/deep/ .el-card__header {
  font-weight: bold;
  font-size: 16px;
  padding: 15px 20px;
  border-bottom: 1px solid #EBEEF5;
}

/deep/ .el-card__body {
  padding: 20px;
}

/deep/ .charge-info-dialog {
  margin-top: 5vh !important;
  margin-bottom: 5vh !important;

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
    overflow: hidden;
    max-height: calc(90vh - 20px);
  }
}

.dialog-header {
  position: relative;
  height: 40px;
  padding: 10px 20px 0;
  text-align: right;

  .close-btn {
    font-size: 20px;
    color: #909399;
    padding: 0;
    width: 30px;
    height: 30px;
    line-height: 30px;

    &:hover {
      color: #303133;
    }
  }
}

.charge-info-container {
  overflow: hidden;
}
</style>
